import jsPDF from 'jspdf';
import axios from 'axios';
import moment from 'moment';
import { v1 as uuidv1 } from 'uuid';

import { db, storage } from 'firebase/fbConfig.js';
import { LOGO } from 'variables/documents';
import { primaryColor } from 'assets/jss/material-dashboard-pro-react'


const TYPE_TEXT = 'text';
const TYPE_IMAGE = 'image';
const TYPE_RECT = 'rect';
const TYPE_LINE = 'line';
const TYPE_MULTI_LINE_TEXT = 'multiLineText';
const TYPE_CHECKBOX = 'checkbox';

const X_MARGIN = 36;
const Y_MARGIN = 20;

function pdfGenerator() {
    /**
    * Possible structures for the data Item
    * {*  type :  Text | MultiLineText | Image | Rect | Line ,
    *  text : String for Text
    * x : The starting X position of any Type | The end X position, in a case where the text | multiLineText is right Aligned,
    * y : The starting Y position of any Type | The end Y position, in a case where the text / multiLineText is right Aligned,
    * base64 : The base64 representation of an image,
    * width : Width of Image / Rect,
    * height : Height of Image / Rect,
    * x1 : Start X for line,
    * x2 : End X for line,
    * y1 : Start Y for line,
    * y2 : End X for line,
    * texts : Array of String[String] for MultiLineText,
    * style : An Object similar to @defaultStyle above
    * }
    *
    *
    1. Text: text, x, y, style
    2. Image: base64, x, y, width, height, makeLink
    3. Rect: x, y, width, height, style
    4. MultiLineText: texts:[text], x, y, style
    5. Line: x1, x2, y1, y2, style
    6. CheckBox: x, y, width, height, checked style
       */

    function addDataToPage(data, doc) {
        if (isValidData(data) && doc !== undefined) {
            data.forEach(d => {
                PROCESSING_FUNC[d.type](d, doc);
            });
            return doc;
        }
    }

    /**
     * Default Styles
     */
    const defaultStyle = {
        fontWeight: 'normal',
        fontSize: 10,
        verticalSpacing: 12,
        backgroundColor: 'white',
        lineColor: '#262626',
        textColor: '#262626',
        font: 'helvetica',
        textAlign: 'left'
    };

    function resetToDefaultStyle(doc) {
        doc.setFont(defaultStyle.font, defaultStyle.fontWeight);
        doc.setFontSize(defaultStyle.fontSize);
        doc.setTextColor(defaultStyle.textColor)
        doc.setDrawColor(defaultStyle.lineColor)
    }

    const SUPPORTED_TYPES = [TYPE_TEXT, TYPE_IMAGE, TYPE_RECT, TYPE_MULTI_LINE_TEXT, TYPE_LINE, TYPE_CHECKBOX]

    const DATA_ERROR_MESSAGES = {
        dataRequired: 'Data is required',
        dataMustBeArray: 'Data must be an array',
        dataItemMustBeObject: 'Each Item in Data must be an Object with defined Type',
        invalidType: 'Invalid Type :  Supported Types = ' + SUPPORTED_TYPES,
        xAndYRequired: 'Fields x and Y are required for all types except Line'
    }


    const TEXT_ERROR_MESSAGES = {
        textRequired: 'text field is required'
    }

    const IMAGE_ERROR_MESSAGES = {
        base64Required: 'base64 field is required',
        widthRequired: 'width field is required',
        heightRequired: 'height field is required',
        urlRequired: 'url field is required'
    }

    const MULTI_LINE_TEXT_ERROR_MESSAGES = {
        textsRequired: 'texts field is required and must be Array of Strings'
    }

    const RECT_ERROR_MESSAGES = {
        widthRequired: 'fields width is required',
        height: 'fields height is required'
    }

    const CHECKBOX_ERROR_MESSAGES = {
        widthRequired: 'fields width is required',
        height: 'fields height is required'
    }

    const LINE_ERROR_MESSAGES = {
        pointsRequired: 'Points is required',
    };

    function processText(d, doc) {
        let { x, y } = d;
        if (d.style) {
            const { fontWeight, fontSize, textColor, textAlign } = d.style;
            doc.setFont(defaultStyle.font, fontWeight || defaultStyle.fontWeight);
            doc.setFontSize(fontSize || defaultStyle.fontSize);
            doc.setTextColor(textColor || defaultStyle.textColor);
            if (textAlign && textAlign === 'right') {
                x -= doc.getTextWidth(d.text);
            } else if (textAlign && textAlign === 'center') {
                x -= doc.getTextWidth(d.text) / 2;
            }
        }
        doc.text(d.text, x, y);
        resetToDefaultStyle(doc);
    }

    function processLine(d, doc) {
        if (d.style) {
            if (d.style.lineColor) {
                doc.setDrawColor(d.style.lineColor)
            }
        }
        doc.line(d.x1, d.y1, d.x2, d.y2);
        resetToDefaultStyle(doc);
    }

    function processCheckBox(d, doc) {
        const {
            CheckBox,
        } = doc.AcroForm;
        const field = new CheckBox();
        field.Rect = [d.x, d.y, d.width, d.height];
        field.appearanceState = d.checked ? 'On' : 'Off'
        field.maxFontSize = 14;
        doc.addField(field);
        resetToDefaultStyle(doc)
    }

    function processImage(d, doc) {
        const { fileType } = doc.getImageProperties(d.base64);
        doc.addImage(d.base64, fileType, d.x, d.y, d.width, d.height);
        resetToDefaultStyle(doc);
    }

    function processMultiLine(d, doc) {
        let verticalSpacing = defaultStyle.verticalSpacing;
        if (d.style && d.style.verticalSpacing) {
            verticalSpacing = d.style.verticalSpacing;
        }
        d.texts.forEach((e, index) => {
            processText(Object.assign({}, d, { text: e, y: d.y + index * verticalSpacing }), doc);
        })
        resetToDefaultStyle(doc)
    }

    function processRect(d, doc) {
        doc.setFillColor(defaultStyle.backgroundColor)
        doc.setDrawColor('#ffffff')
        if (d.style) {
            if (d.style.backgroundColor) {
                doc.setFillColor(d.style.backgroundColor)
                doc.setDrawColor(d.style.drawColor || '#ffffff')
            }
        }
        doc.rect(d.x, d.y, d.width, d.height, 'FD')
    }



    const PROCESSING_FUNC = {
        [TYPE_TEXT]: processText,
        [TYPE_IMAGE]: processImage,
        [TYPE_MULTI_LINE_TEXT]: processMultiLine,
        [TYPE_LINE]: processLine,
        [TYPE_RECT]: processRect,
        [TYPE_CHECKBOX]: processCheckBox
    }

    const VALIDATOR = {
        [TYPE_TEXT]: isValidText,
        [TYPE_IMAGE]: isValidImage,
        [TYPE_LINE]: isValidLine,
        [TYPE_MULTI_LINE_TEXT]: isValidMultiLineText,
        [TYPE_RECT]: isValidRect,
        [TYPE_CHECKBOX]: isValidCheckBox
    }

    function isValidData(data) {
        if (data === undefined)
            throw new Error(DATA_ERROR_MESSAGES.dataRequired);
        if (!Array.isArray(data))
            throw new Error(DATA_ERROR_MESSAGES.dataMustBeArray);
        data.forEach(d => {
            if (!d || !d.type)
                throw new Error(DATA_ERROR_MESSAGES.dataItemMustBeObject);
        })
        data.forEach(d => {
            if (!SUPPORTED_TYPES.includes(d.type))
                throw new Error(DATA_ERROR_MESSAGES.invalidType);
        })

        data.forEach(d => {
            if (VALIDATOR[d.type]) {
                VALIDATOR[d.type](d);
            }
        })

        return true;
    }

    function isValidText(item) {
        if (item.text === undefined)
            throw new Error(TEXT_ERROR_MESSAGES.textRequired);
        return true;
    }

    function isValidLine(item) {
        if ((isNaN(item.x1) || isNaN(item.x2) || isNaN(item.y1) || isNaN(item.y2)))
            throw new Error(LINE_ERROR_MESSAGES.pointsRequired);
        return true;
    }

    function isValidImage(item) {
        if (item.base64 === undefined || item.base64.length === 0)
            throw new Error(IMAGE_ERROR_MESSAGES.base64Required);
        if (isNaN(item.width) || item.width < 1)
            throw new Error(IMAGE_ERROR_MESSAGES.widthRequired);
        if (isNaN(item.height) || item.width < 1)
            throw new Error(IMAGE_ERROR_MESSAGES.heightRequired);
        if (item.makeLink && !item.url)
            throw new Error(IMAGE_ERROR_MESSAGES.urlRequired)
        return true;
    }

    function isValidMultiLineText(item) {
        if (!Array.isArray(item.texts))
            throw new Error(MULTI_LINE_TEXT_ERROR_MESSAGES.textsRequired);
        return true;
    }

    function isValidRect(item) {
        if (isNaN(item.width))
            throw new Error(RECT_ERROR_MESSAGES.width)
        if (isNaN(item.height))
            throw new Error(RECT_ERROR_MESSAGES.height)
        return true;
    }

    function isValidCheckBox(item) {
        if (isNaN(item.width))
            throw new Error(CHECKBOX_ERROR_MESSAGES.width)
        if (isNaN(item.height))
            throw new Error(CHECKBOX_ERROR_MESSAGES.height)
        return true;
    }
    return {
        addDataToPage
    }
}

const normalPDF = () => {
    const doc = new jsPDF({
        orientation: 'p',
        unit: 'pt',
        format: [612, 792]
    });
    const pdfGen = pdfGenerator();

    const { width, height } = doc.internal.pageSize;
    const innerWidth = width - 2 * X_MARGIN;
    const innerHeight = height - 2 * Y_MARGIN;

    return {
        doc,
        pdfGen,
        width,
        height,
        innerWidth,
        innerHeight
    }
}

const remNonAscii = (str) => str.replace(new RegExp('[^\x1F-\x7F]+'), '');

const uploadFile = (refString, file, callback = () => null, ...callbackArgs) => new Promise((resolve, reject) => {
    const storageRef = storage.ref(refString);
    // upload file64
    const uploadTask = storageRef.put(file);
    // update progress bar
    uploadTask.on('state_changed', (snapshot) => {
        // Observe state change events such as progress, pause, and
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        callback(progress, ...callbackArgs);

    }, (error) => {
        // Handle unsuccessful uploads
        console.log(error);
        reject(error);
    }, () => {
        // Handle successful uploads on complete
        // For instance, get the download URL: https://firebasestorage.googleapis.com/...
        uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
            resolve(downloadURL);
        });
    });
})

const createSignable = (data) => new Promise((resolve, reject) => {
    axios({
        method: 'POST',
        // url: `https://arcane-lowlands-31408.herokuapp.com/https://api.eversign.com/api/document?access_key=751efbc709ec4ace1d6ff70a062b7243&business_id=246480`,
        url: `https://api.eversign.com/api/document?access_key=751efbc709ec4ace1d6ff70a062b7243&business_id=246480`,
        data
    })
        .then(res => resolve(res.data));
})

const createBOL = (order, vendorSelected = ' ', esignEnabled) => new Promise((resolve, reject) => {
    const {
        doc,
        pdfGen,
        width,
        height,
        innerWidth,
        innerHeight
    } = normalPDF();

    // PDF HEADER
    const header = [
        {
            type: TYPE_IMAGE,
            base64: LOGO,
            width: 70,
            height: 20,
            x: X_MARGIN,
            y: Y_MARGIN - 10
        },
        {
            type: TYPE_TEXT,
            text: "BILL OF LADING",
            x: width / 2,
            y: Y_MARGIN,
            style: {
                textAlign: 'center',
                textColor: "black",
                fontWeight: 'bold'
            }
        },
        {
            type: TYPE_TEXT,
            text: "BOL #" + order.freightBillNumber,
            x: width / 2,
            y: Y_MARGIN + 15,
            style: {
                textAlign: 'center',
                textColor: "black",
            }
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    '(303) 690-1618',
                    'operations@cargorx.com',
                ],
            x: width - X_MARGIN,
            y: Y_MARGIN,
            style: {
                textAlign: 'right',
                textColor: "black",
                fontWeight: 'bold'
            }
        }
    ];

    const getEntityInformation = (entity) => ({
        businessName: entity.businessName,
        address: entity.address,
        city_state_zip: `${entity.city}, ${entity.state} ${entity.zip}`,
        contactName: `${entity.firstName} ${entity.lastName}`,
        Phone: entity.phone,
        email: entity.email,
        pickupNumber: entity.referenceID ? entity.referenceID : " ",
        specialInstruction: entity.customExtraService ? entity.customExtraService : " "
    });

    const getDate = (date) => {
        if (!date) return ' ';
        if (date.seconds) return date.toDate();
        if (date instanceof Date) return date;
    }

    // ORIGIN INFORMATION
    const shipperInformation = {
        y: 90,
        pickUpDate:
            order.origin.startTimeWindow ?
                (order.origin.date ? `${moment(order.origin.date.seconds ? order.origin.date.toDate() : order.origin.date).format('YYYY-MM-DD')} ${order.origin.startTimeWindow}` : ' ')
                : (order.origin.date ? moment(order.origin.date.seconds ? order.origin.date.toDate() : order.origin.date).format('YYYY-MM-DD HHmm') : ' '),
        ...getEntityInformation(order.origin)
    }

    const shipperInformationData = [
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    "CargoRx, Ltd. is a property broker, and not a motor carrier, and unless superseded in an agreement signed by authorized representatives of CargoRx, Ltd. and the Shipper or Consignee identified herein, all services",
                    "rendered by CargoRx, Ltd. are subject to the terms and conditions available at www.cargorx.com/terms/."
                ],
            x: X_MARGIN + 2,
            y: 44,
            style: {
                textColor: 'black',
                fontSize: 5.5,
                verticalSpacing: 8
            }
        },
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 22,
            x: X_MARGIN,
            y: 56,
            style: {
                backgroundColor: primaryColor,
            }
        },
        {
            type: TYPE_TEXT,
            text: "Shipper Information",
            x: X_MARGIN + 2,
            y: 70, style: {
                textColor: "white"
            }
        },
        {
            type: TYPE_TEXT,
            text: "Pickup Date: " + shipperInformation.pickUpDate,
            x: X_MARGIN + innerWidth / 3,
            y: 70, style: {
                textColor: "white"
            }
        },
        {
            type: TYPE_TEXT,
            text: "Pick Up #: " + shipperInformation.pickupNumber,
            x: X_MARGIN + (3 * (innerWidth / 4)),
            y: 70, style: {
                textColor: "white"
            }
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    doc.splitTextToSize("Location: " + shipperInformation.businessName + " - " + shipperInformation.address + " " + shipperInformation.city_state_zip, 275)

                    // "Location: " + shipperInformation.businessName,
                    // "Address: " + shipperInformation.address,
                    // "" + shipperInformation.city_state_zip
                ],
            x: X_MARGIN,
            y: shipperInformation.y
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    "Name: " + shipperInformation.contactName,
                    "Phone: " + shipperInformation.Phone,
                    doc.splitTextToSize("Email: " + shipperInformation.email, 290)
                ],
            x: X_MARGIN + innerWidth / 3,
            y: shipperInformation.y
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    // "Pick Up " + shipperInformation.pickupNumber,
                    doc.splitTextToSize("Special Instructions: " + remNonAscii(shipperInformation.specialInstruction).substring(0, 100), 290)
                ],
            x: X_MARGIN + 2 * (innerWidth / 3),
            y: shipperInformation.y
        }
    ];
 
    // DESTINATION INFORMATION
    const consigneeInformation = {
        y: 160,
        deliverByDate: order.deliveryType == 'Ground' ?
            (order.ground ? (moment(getDate(order.ground.quoteETA)).format('YYYY-MM-DD') + " " + ([order.ground.start, order.ground.end].filter(Boolean).join(' - '))) : ' ')
            : order.deliveryType == 'Additional Options' ?
                (order.ltl ? (moment(getDate(order.ltl.quoteETA)).format('YYYY-MM-DD') + " " + ([order.ltl.start, order.ltl.end].filter(Boolean).join(' - '))) : ' ')
                : ' ',
        ...getEntityInformation(order.destination)
    }

    const consigneeInformationData = [
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 22,
            x: X_MARGIN,
            y: 126,
            style: {
                backgroundColor: primaryColor,
            }
        },
        {
            type: TYPE_TEXT,
            text: "Consignee Information",
            x: X_MARGIN + 2,
            y: 140, style: {
                textColor: "white"
            }
        },
        {
            type: TYPE_TEXT,
            text: "Deliver By Date: " + consigneeInformation.deliverByDate,
            x: X_MARGIN + innerWidth / 3,
            y: 140, style: {
                textColor: "white"
            }
        },
        {
            type: TYPE_TEXT,
            text: "Pick Up #: " + consigneeInformation.pickupNumber,
            x: X_MARGIN + (3 * (innerWidth / 4)),
            y: 140, style: {
                textColor: "white"
            }
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    doc.splitTextToSize("Location: " + consigneeInformation.businessName + " - " + consigneeInformation.address + " " + consigneeInformation.city_state_zip, 275)
                ],
            x: X_MARGIN,
            y: consigneeInformation.y
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    "Name: " + consigneeInformation.contactName,
                    "Phone: " + consigneeInformation.Phone,
                    doc.splitTextToSize("Email: " + consigneeInformation.email, 290)
                ],
            x: X_MARGIN + innerWidth / 3,
            y: consigneeInformation.y
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    // "Pick Up " + consigneeInformation.pickupNumber,
                    doc.splitTextToSize("Special Instructions: " + remNonAscii(consigneeInformation.specialInstruction).substring(0, 100), 290)
                ],
            x: X_MARGIN + 2 * (innerWidth / 3),
            y: consigneeInformation.y
        }
    ];

    // THIRD PARTY INFORMATION
    const thirdPartyInformation = order.thirdParty && order.thirdParty.email ? {
        y: 230,
        ...getEntityInformation(order.thirdParty)
    } : null;

    const thirdPartyInformationData = order.thirdParty && order.thirdParty.email ? [
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 22,
            x: X_MARGIN,
            y: 196,
            style: {
                backgroundColor: primaryColor,
            }
        },
        {
            type: TYPE_TEXT,
            text: "Third Party Information",
            x: X_MARGIN + 2,
            y: 210, style: {
                textColor: "white"
            }
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    doc.splitTextToSize("Location: " + thirdPartyInformation.businessName + " - " + thirdPartyInformation.address + " " + thirdPartyInformation.city_state_zip, 290)

                    // "Location: " + thirdPartyInformation.businessName,
                    // "Address: " + thirdPartyInformation.address,
                    // "" + thirdPartyInformation.city_state_zip
                ],
            x: X_MARGIN,
            y: thirdPartyInformation.y
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    "Name: " + thirdPartyInformation.contactName,
                    "Phone: " + thirdPartyInformation.Phone,
                    doc.splitTextToSize("Email: " + thirdPartyInformation.email, 290)
                ],
            x: X_MARGIN + innerWidth / 3,
            y: thirdPartyInformation.y
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    // "Pick Up: " + thirdPartyInformation.pickupNumber,
                    " "
                ],
            x: X_MARGIN + 2 * (innerWidth / 3),
            y: thirdPartyInformation.y
        }
    ] : [];

    // BILLING PARTY INFORMATION
    const freightSection = {
        y: 278,
        'prepaid': order.billingParty == 'Shipper' ? true : false,
        'collect': order.billingParty == 'Consignee' ? true : false,
        'thirdParty': order.billingParty == 'Third Party' ? true : false
    }

    const freightSectionData = [
        {
            type: TYPE_TEXT,
            text: "Freight Terms",
            x: X_MARGIN + 2,
            y: freightSection.y
        },
        {
            type: TYPE_LINE,
            x1: X_MARGIN + 2,
            y1: freightSection.y + 5,
            x2: X_MARGIN + 70,
            y2: freightSection.y + 5,
            style: {
                lineColor: 'black'
            }
        },
        {
            type: TYPE_TEXT,
            text: "Prepaid",
            x: X_MARGIN + 2 + innerWidth / 4,
            y: freightSection.y
        },
        {
            type: TYPE_LINE,
            x1: X_MARGIN + 2 + innerWidth / 4,
            y1: freightSection.y + 5,
            x2: X_MARGIN + 2 + innerWidth / 4 + 70,
            y2: freightSection.y + 5,
            style: {
                lineColor: 'black'
            }
        },
        {
            type: TYPE_CHECKBOX,
            width: 15,
            height: 15,
            x: X_MARGIN + 2 + innerWidth / 4 + 50,
            y: freightSection.y - 12,
            checked: freightSection.prepaid
        },
        {
            type: TYPE_TEXT,
            text: "Collect",
            x: X_MARGIN + 2 + innerWidth / 2,
            y: freightSection.y
        },
        {
            type: TYPE_LINE,
            x1: X_MARGIN + 2 + innerWidth / 2,
            y1: freightSection.y + 5,
            x2: X_MARGIN + 2 + innerWidth / 2 + 70,
            y2: freightSection.y + 5,
            style: {
                lineColor: 'black'
            }
        },
        {
            type: TYPE_CHECKBOX,
            width: 15,
            height: 15,
            x: X_MARGIN + 2 + innerWidth / 2 + 50,
            y: freightSection.y - 12,
            checked: freightSection.collect
        },
        {
            type: TYPE_TEXT,
            text: "Third Party",
            x: X_MARGIN + 2 + innerWidth * 3 / 4,
            y: freightSection.y
        },
        {
            type: TYPE_LINE,
            x1: X_MARGIN + 2 + innerWidth * 3 / 4,
            y1: freightSection.y + 5,
            x2: X_MARGIN + 2 + innerWidth * 3 / 4 + 74,
            y2: freightSection.y + 5,
            style: {
                lineColor: 'black'
            }
        },
        {
            type: TYPE_CHECKBOX,
            width: 15,
            height: 15,
            x: X_MARGIN + 2 + innerWidth * 3 / 4 + 55,
            y: freightSection.y - 12,
            checked: freightSection.thirdParty
        },
    ];

    // CARGORX INFORMATION
    const cargoRXInformation = {
        y: 324,
        'contact': '',
        'Phone': '(303) 690-1618',
        'email': 'operations@cargorx.com',
        'cargoRxShipmentNumber': order.freightBillNumber,
    }

    const cargoRXInformationData = [
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 22,
            x: X_MARGIN,
            y: 285,
            style: {
                backgroundColor: primaryColor,
            }
        },
        {
            type: TYPE_TEXT,
            text: "CargoRx Contact Information",
            x: X_MARGIN + 2,
            y: 301, style: {
                textColor: "white"
            }
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    "Contact: " + cargoRXInformation.contact,
                    "Phone: " + cargoRXInformation.Phone,
                ],
            x: X_MARGIN,
            y: cargoRXInformation.y
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    "Email: " + cargoRXInformation.email,
                    "Cargo Rx Shipment Number: " + cargoRXInformation.Phone,
                ],
            x: X_MARGIN + innerWidth / 2,
            y: cargoRXInformation.y
        }
    ]

    // VENDOR INFORMATION
    const carrierInformation = {
        y: 330,
        emergencyResponseNumber: order.items.map(item => item.emergencyResponseNumber).join(" "),
        carrierName: vendorSelected,
    }

    const carrierInformationData = [
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 22,
            x: X_MARGIN,
            y: 290,
            style: {
                backgroundColor: primaryColor,
            }
        },
        {
            type: TYPE_TEXT,
            text: "Carrier Information",
            x: X_MARGIN + 2,
            y: 305, style: {
                textColor: "white"
            }
        },
        {
            type: TYPE_TEXT,
            text: "Carrier Name: " + carrierInformation.carrierName,
            x: X_MARGIN,
            y: carrierInformation.y
        },
    ];

    // ITEM INFORMATION
    const itemInfoArray = [];

    let totalWeight = 0;
    let totalQuantity = 0;
    let totalDecValue = 0;
    let totalDescriptionHeight = 0;

    order.items.map((item, i) => {

        // CREATE DESCRIPTION OF ITEM; GET DIMENSIONS
        const separatedDescription = [item.packaging, item.unNumber, item.properShippingName, item.emergencyResponseNumber].filter(Boolean).join(' - ');
        const descriptionDimensions = doc.getTextDimensions(doc.splitTextToSize(separatedDescription, 600));

        totalDescriptionHeight += Number(descriptionDimensions.h * .7) + 10;
        totalDecValue += Number(isNaN(item.declaredValue) ? 0 : item.declaredValue);

        // ADD QUANTITY AND WEIGHT TO TOTALS
        const qty = Number(item.quantity ? item.quantity : 0);
        const wpp = Number(item.weight ? item.weight : 0);
        totalQuantity += qty;
        totalWeight += qty * wpp;

        const extraInfo = {
            y: 355 + totalDescriptionHeight,
            pieces: item.quantity ? item.quantity : " ",
            description: separatedDescription ? `${separatedDescription}, ${item.description}` : item.description,
            weight: item.weight ? item.weight : " ",
            length: item.length ? item.length : " ",
            width: item.width ? item.width : " ",
            height: item.height ? item.height : " ",
            haz: item.isHazmat ? "X" : " ",
            un_num: item.unNumber ? item.unNumber : " ",
            sum_quantity: ' ',
            sum_weight: ' '
        }

        const itemInfoData = [
            {
                type: TYPE_RECT,
                width: width - 2 * X_MARGIN,
                height: i == 0 ? 22 : 1,
                x: X_MARGIN,
                y: extraInfo.y - (i == 0 ? 17 : 0),
                style: {
                    backgroundColor: primaryColor,
                }
            },
            {
                type: TYPE_TEXT,
                text: i == 0 ? "Pieces" : " ",
                x: X_MARGIN,
                y: extraInfo.y,
                style: {
                    textColor: "white",
                    fontWeight: 'bold',
                    fontSize: "8"
                }
            },
            {
                type: TYPE_TEXT,
                text: i == 0 ? "Weight" : " ",
                x: X_MARGIN + (innerWidth / 16),
                y: extraInfo.y,
                style: {
                    textColor: "white",
                    fontWeight: 'bold',
                    fontSize: "8"
                }
            },
            {
                type: TYPE_TEXT,
                text: i == 0 ? "Length" : " ",
                x: X_MARGIN + 2 * (innerWidth / 16),
                y: extraInfo.y,
                style: {
                    textColor: "white",
                    fontWeight: 'bold',
                    fontSize: "8"
                }
            },
            {
                type: TYPE_TEXT,
                text: i == 0 ? "Width" : " ",
                x: X_MARGIN + 3 * (innerWidth / 16),
                y: extraInfo.y,
                style: {
                    textColor: "white",
                    fontWeight: 'bold',
                    fontSize: "8"
                }
            },
            {
                type: TYPE_TEXT,
                text: i == 0 ? "Height" : " ",
                x: X_MARGIN + 4 * (innerWidth / 16),
                y: extraInfo.y,
                style: {
                    textColor: "white",
                    fontWeight: 'bold',
                    fontSize: "8"
                }
            },
            {
                type: TYPE_TEXT,
                text: i === 0 ? "Hazmat" : " ",
                x: X_MARGIN + 5 * (innerWidth / 16),
                y: extraInfo.y,
                style: {
                    textColor: "white",
                    fontWeight: 'bold',
                    fontSize: "8"
                }
            },
            {
                type: TYPE_TEXT,
                text: i == 0 ? "Description" : " ",
                x: X_MARGIN + 6 * (innerWidth / 16),
                y: extraInfo.y,
                style: {
                    textColor: "white",
                    fontWeight: 'bold',
                    fontSize: "8"
                }
            },
        ]

        const itemInfoValueData = [
            {
                type: TYPE_TEXT,
                text: extraInfo.pieces,
                x: X_MARGIN,
                y: extraInfo.y + 17
            },
            {
                type: TYPE_TEXT,
                text: extraInfo.weight,
                x: X_MARGIN + (innerWidth / 16),
                y: extraInfo.y + 17
            },
            {
                type: TYPE_TEXT,
                text: extraInfo.length,
                x: X_MARGIN + 2 * (innerWidth / 16),
                y: extraInfo.y + 17
            },
            {
                type: TYPE_TEXT,
                text: extraInfo.width,
                x: X_MARGIN + 3 * (innerWidth / 16),
                y: extraInfo.y + 17
            },
            {
                type: TYPE_TEXT,
                text: extraInfo.height,
                x: X_MARGIN + 4 * (innerWidth / 16),
                y: extraInfo.y + 17
            },
            {
                type: TYPE_TEXT,
                text: extraInfo.haz,
                x: X_MARGIN + 5 * (innerWidth / 16),
                y: extraInfo.y + 17
            },
            {
                type: TYPE_TEXT,
                text: doc.splitTextToSize(extraInfo.description, 600),
                x: X_MARGIN + 6 * (innerWidth / 16),
                y: extraInfo.y + 17
            },

        ]

        itemInfoArray.push(...itemInfoData, ...itemInfoValueData)

    })

    const totalValueInformation = {
        y: totalDescriptionHeight > 120 ? 650 : 380,
        sum_quantity: totalQuantity.toString(),
        sum_weight: totalWeight.toString(),
    }

    const totalValueData = [
        {
            type: TYPE_TEXT,
            text: `Totals:`,
            x: X_MARGIN,
            y: totalValueInformation.y + 97
        },
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 1,
            x: X_MARGIN,
            y: totalValueInformation.y + 102,
            style: {
                backgroundColor: primaryColor,
            }
        },
        {
            type: TYPE_TEXT,
            text: `${totalQuantity}`,
            x: X_MARGIN + 5,
            y: totalValueInformation.y + 115
        },
        {
            type: TYPE_TEXT,
            text: `${totalWeight}`,
            x: X_MARGIN + 35,
            y: totalValueInformation.y + 115
        },
    ]

    // ADD DYNAMIC DATA TO PDF
    pdfGen.addDataToPage([
        ...header,
        ...shipperInformationData,
        ...consigneeInformationData,
        ...thirdPartyInformationData,
        ...freightSectionData,
        ...carrierInformationData,
        ...totalValueData,
        ...itemInfoArray,
    ], doc);

    // STATIC INFORMATION
    const declareInfo = {
        y: (totalDescriptionHeight > 120) ? 75 : 540,
        'decValue': "$" + totalDecValue,
        'shipperSignature': " ?",
        'shipperPrintedName': " ?",
        'carrierSignature': " >",
        'carrierPrintedName': " ?",
        'receivedSignature': " ?",
        'receivedPrintedName': "? ",
        'note3Value': totalDecValue
    };

    let y = declareInfo.y
    const declareData = [
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 60,
            x: X_MARGIN,
            y: y - 36,
            style: {
                backgroundColor: 'white',
                drawColor: 'black'
            }
        },
        {
            type: TYPE_TEXT,
            text: 'Note:(1) ',
            x: X_MARGIN + 2,
            y: y - 28,
            style: {
                fontWeight: 'bold',
                textColor: 'black',
                fontSize: 6.5,
            }
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
            [
                "\t\t  Carrier is liable for loss, damage or destruction to cargo, including due to unreasonable delay, pursuant to 49 U.S.C. § 14706. Where the Carrier identified herein is CargoRx Dry Capacity, such liability is ",
                "limited to the lesser of the cost to repair or replace the goods or: (1) for new and unused cargo, $5.00 per pound per package subject to a maximum liability of $100,000 per trailer or conveyance; or (2) for used or",
                "reconditioned equipment, $1.00 per pound subject to a maximum liability of $10,000 per trailer or conveyance. If Shipper wishes to increase the amount of this liability, it may do so in accordance with CargoRx Dry",
                "Capacity's terms and conditions of service at www.cargorx.com/terms/ or by contacting CargoRx Dry Capacity at 303-690-1618 and paying additional charges related thereto. Where any other party is identified as Carrier,",
                "Carrier agrees that all services are subject to the rates and contracts agreed upon in writing between Carrier and CargoRx, Ltd. and that Carrier's services are not subject to any tariffs or classifications whether",
                "individually determined or filed with any federal or state regulatory agency, or maintained by the Carrier."
            ],
            x: X_MARGIN + 2,
            y: y - 28,
            style: {
                textColor: 'black',
                fontSize: 5.5,
                verticalSpacing: 8
            }
        },

        // {
        //     type: TYPE_RECT,
        //     width: width - 2 * X_MARGIN,
        //     height: 16,
        //     x: X_MARGIN,
        //     y: y + 4,
        //     style: {
        //         backgroundColor: 'white',
        //         drawColor: 'black'
        //     }
        // },
        // {
        //     type: TYPE_TEXT,
        //     text: "",
        //     x: X_MARGIN + 2,
        //     y: y - 8,
        //     style: {
        //         fontWeight: 'bold',
        //         textColor: 'black'
        //     }
        // },
        // {
        //     type: TYPE_TEXT,
        //     text: 'Note:(2) ',
        //     x: X_MARGIN + 2,
        //     y: y + 12,
        //     style: {
        //         fontWeight: 'bold',
        //         textColor: 'black',
        //         fontSize: 6.5,
        //     }
        // },
        // {
        //     type: TYPE_MULTI_LINE_TEXT,
        //     texts:
        //         [
        //             "\t\t",
        //         ],
        //     x: X_MARGIN + 4,
        //     y: y + 12,
        //     style: {
        //         textColor: 'black',
        //         fontSize: 5.5,
        //         verticalSpacing: 8
        //     }
        // },
        // {
        //     type: TYPE_RECT,
        //     width: width - 2 * X_MARGIN,
        //     height: 36,
        //     x: X_MARGIN,
        //     y: y + 27,
        //     style: {
        //         backgroundColor: 'white',
        //         drawColor: 'black'
        //     }
        // },
        // {
        //     type: TYPE_TEXT,
        //     text: 'Note:(3) ',
        //     x: X_MARGIN + 2,
        //     y: y + 34,
        //     style: {
        //         fontWeight: 'bold',
        //         textColor: 'black',
        //         fontSize: 6.5,
        //     }
        // },
        // {
        //     type: TYPE_TEXT,
        //     text: "3",
        //     x: X_MARGIN + 83,
        //     y: y + 58,
        //     style: {
        //         fontWeight: 'bold',
        //         textColor: 'black',
        //         fontSize: 6.5,
        //     }
        // },
        // {
        //     type: TYPE_MULTI_LINE_TEXT,
        //     texts:
        //         [
        //             "\t\t  Carrier is liable for loss, damage or destruction to cargo, including due to unreasonable delay, pursuant to 49 U.S.C. § 14706. Where the Carrier identified herein is CargoRx Dry Capacity,",
        //             " such liability is limited to the lesser of the cost to repair or replace the goods or: (1) for new and unused cargo, $5.00 per pound per package subject to a maximum liability of $100,000 per trailer or conveyance;",
        //             "or (2) for used or reconditioned equipment, $1.00 per pound subject to a maximum liability of $10,000 per trailer or conveyance. If Shipper wishes to increase the amount of this liability, it may do so in accordance with CargoRx Dry Capacity's terms and conditions of service at www.cargorx.com/terms/",
        //             " or by contacting CargoRx Dry Capacity at 303-690-1618 and paying additional charges related thereto. Where any other party is identified as Carrier, Carrier agrees that all services are subject to the rates and contracts agreed upon in writing between Carrier and CargoRx, Ltd. and that Carrier's services are not subject to any tariffs or classifications whether individually determined or filed with any federal or state regulatory agency, or maintained by the Carrier."
        //         ],
        //     x: X_MARGIN + 2,
        //     y: y + 34,
        //     style: {
        //         textColor: 'black',
        //         fontSize: 5.5,
        //         verticalSpacing: 8
        //     }
        // },
        // {
        //     type: TYPE_RECT,
        //     width: width - 2 * X_MARGIN,
        //     height: 16,
        //     x: X_MARGIN,
        //     y: y + 68,
        //     style: {
        //         backgroundColor: 'white',
        //         drawColor: 'black'
        //     }
        // },
        // {
        //     type: TYPE_TEXT,
        //     text: 'Note:(4) ',
        //     x: X_MARGIN + 2,
        //     y: y + 76,
        //     style: {
        //         fontWeight: 'bold',
        //         textColor: 'black',
        //         fontSize: 6.5,
        //     }
        // },
        // {
        //     type: TYPE_MULTI_LINE_TEXT,
        //     texts:
        //         [
        //             "\t\t    ",
        //         ],
        //     x: X_MARGIN + 2,
        //     y: y + 76,
        //     style: {
        //         textColor: 'black',
        //         fontSize: 5.5,
        //         verticalSpacing: 8
        //     }
        // }

    ];

    y = declareInfo.y + 24
    const shippersData = [
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 52,
            x: X_MARGIN,
            y: y + 65,
            style: {
                backgroundColor: 'white',
                drawColor: 'black'
            }
        },
        {
            type: TYPE_TEXT,
            text: 'Shipper Signature ',
            x: X_MARGIN + 2,
            y: y + 82,
            style: {
                fontWeight: 'bold',
                textColor: 'black',
            }
        },
        {
            type: TYPE_LINE,
            x1: X_MARGIN + innerWidth / 8 + 22,
            y1: y + 84,
            x2: X_MARGIN + 2 + innerWidth / 4 + 70,
            y2: y + 84,
            style: {
                lineColor: 'black'
            }
        },
        {
            type: TYPE_TEXT,
            text: 'Printed Name ',
            x: X_MARGIN + innerWidth / 2,
            y: y + 82,
            style: {
                fontWeight: 'bold',
                textColor: 'black',
            }
        },
        {
            type: TYPE_LINE,
            x1: X_MARGIN + innerWidth / 2 + 64,
            y1: y + 84,
            x2: X_MARGIN + 2 + innerWidth * 3 / 4 + 70,
            y2: y + 84,
            style: {
                lineColor: 'black'
            }
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    "This is to certify that the above-named materials are properly classified, packaged, marked, and labeled, and are in proper condition for transportation according to the applicable regulations of the DOT.  The carrier",
                    "shall not make delivery of this shipment without payment of chagres and all other lawful fees.  FOR FREIGHT COLLECT SHIPMENTS: If this shipment is to be delivered to the consignee, without recourse on the",
                    "consignor, the consignor shall sign the following statement.The carrier may decline to make delivery of this shipment without payment of freight and all other lawful charges."
                ],
            x: X_MARGIN + 2,
            y: y + 92,
            style: {
                textColor: 'black',
                fontSize: 5.5,
                verticalSpacing: 8
            }
        },

    ]

    y = declareInfo.y + 81
    const carriersData = [
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 42,
            x: X_MARGIN,
            y: y + 65,
            style: {
                backgroundColor: 'white',
                drawColor: 'black'
            }
        },
        {
            type: TYPE_TEXT,
            text: 'Carrier Signature ',
            x: X_MARGIN + 2,
            y: y + 82,
            style: {
                fontWeight: 'bold',
                textColor: 'black',
            }
        },
        {
            type: TYPE_LINE,
            x1: X_MARGIN + innerWidth / 8 + 22,
            y1: y + 84,
            x2: X_MARGIN + 2 + innerWidth / 4 + 70,
            y2: y + 84,
            style: {
                lineColor: 'black'
            }
        },
        {
            type: TYPE_TEXT,
            text: 'Printed Name ',
            x: X_MARGIN + innerWidth / 2,
            y: y + 82,
            style: {
                fontWeight: 'bold',
                textColor: 'black',
            }
        },
        {
            type: TYPE_LINE,
            x1: X_MARGIN + innerWidth / 2 + 64,
            y1: y + 84,
            x2: X_MARGIN + 2 + innerWidth * 3 / 4 + 70,
            y2: y + 84,
            style: {
                lineColor: 'black'
            }
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    "Carrier acknowledges receipt of packages and required placards. Carrier certifies emergency response information was made available and/or carrier has the DOT emergency response guidebook or equivalent",
                    "documentation in the vehicle. Property described above is received in good order, except as noted. "
                ],
            x: X_MARGIN + 2,
            y: y + 92,
            style: {
                textColor: 'black',
                fontSize: 5.5,
                verticalSpacing: 8
            }
        },

    ]

    y = declareInfo.y + 127
    const receiversData = [
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 36,
            x: X_MARGIN,
            y: y + 65,
            style: {
                backgroundColor: 'white',
                drawColor: 'black'
            }
        },
        {
            type: TYPE_TEXT,
            text: 'Received By Signature ',
            x: X_MARGIN + 2,
            y: y + 82,
            style: {
                fontWeight: 'bold',
                textColor: 'black',
            }
        },
        {
            type: TYPE_LINE,
            x1: X_MARGIN + innerWidth / 8 + 22,
            y1: y + 84,
            x2: X_MARGIN + 2 + innerWidth / 4 + 70,
            y2: y + 84,
            style: {
                lineColor: 'black'
            }
        },
        {
            type: TYPE_TEXT,
            text: 'Printed Name ',
            x: X_MARGIN + innerWidth / 2,
            y: y + 82,
            style: {
                fontWeight: 'bold',
                textColor: 'black',
            }
        },
        {
            type: TYPE_LINE,
            x1: X_MARGIN + innerWidth / 2 + 64,
            y1: y + 84,
            x2: X_MARGIN + 2 + innerWidth * 3 / 4 + 70,
            y2: y + 84,
            style: {
                lineColor: 'black'
            }
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    "Signature indicates all pieces received in good order unless otherwise stated",
                ],
            x: X_MARGIN + 2,
            y: y + 92,
            style: {
                textColor: 'black',
                fontSize: 5.5,
                verticalSpacing: 8
            }
        },

    ];

    if (totalDescriptionHeight > 120) {
        doc.addPage();
        doc.setPage(2);
    }

    // ADD STATIC DATA TO PAGE
    pdfGen.addDataToPage([
        ...declareData,
        ...shippersData,
        ...carriersData,
        ...receiversData
    ], doc);

    // TURN PDF TO BLOB, CREATE RANDOM ID FOR NEW FILE
    const blobPDF = new Blob([doc.output('blob')], { type: 'application/pdf' });
    const fileID = uuidv1();

    if (esignEnabled) {
 // PUT BLOB INTO FIREBASE STORAGE
 uploadFile(
    `orders/${order.id}/${fileID}`,
    blobPDF
) // CREATE SIGNABLE DOCUMENT WITH EVERGRANDE 
    .then(fileURL => createSignable({
        sandbox: 0,
        is_draft: 0,
        embedded: 1,
        title: `CargoRx Document Bill of Lading ${order.freightBillNumber}`,
        message: 'This is my general document message.',
        use_signer_order: 0,
        reminders: 1,
        require_all_signers: 0,
        custom_requester_name: '',
        custom_requester_email: '',
        redirect: '',
        redirect_decline: '',
        client: '',
        expires: 1750513480,
        embedded_signing_enabled: 1,
        flexible_signing: 0,
        use_hidden_tags: 0,
        files: [
            {
                name: 'Bill of Lading',
                file_url: fileURL,
                file_id: '',
                file_base64: '',
            },
        ],
        signers: [
            {
                id: 1,
                name: ' ',
                email: order.origin.email ? order.origin.email : 'shipperalerts@haneyfreightways.com',
                order: 1,
                role: order.id,
                // "pin": "3874",
                message: 'This is my custom message to Shipper.',
                deliver_email: 0,
                language: 'en',
            },
            {
                id: 2,
                // "name": (tripInfo.driversDropOffData[0].firstName + tripInfo.driversDropOffData[0].firstName),
                name: ' ',
                // "email": (driverPickUpEmail ? driverPickUpEmail : (vendorPickUp.email ? vendorPickUp.email : "vendoralerts@haneyfreightways.com")),
                // "email": "justin@pondata.com",
                email: order.vendorPickUp ?
                    order.vendorPickUp.email ?
                        order.vendorPickUp.email
                        : 'vendoralerts@haneyfreightways.com'
                    : 'vendoralerts@haneyfreightways.com',
                order: 2,
                role: order.id,
                // "pin": "3874",
                message: 'This is my custom message to Driver.',
                deliver_email: 0,
                language: 'en',
            },
            {
                id: 3,
                name: ' ',
                email: order.destination.email ?
                    order.destination.email === order.origin.email ?
                        'consigneealerts@haneyfreightways.com'
                        : order.destination.email
                    : 'consigneealerts@haneyfreightways.com',
                order: 3,
                role: order.id,
                // "pin": "3874",
                message: 'This is my custom message to Consignee.',
                deliver_email: 0,
                language: 'en',
            },
        ],
        recipients: [
            {
                name: 'Customer Alerts',
                email: 'customeralerts@haneyfreightways.com',
                // "email": "justin@pondata.com",
                language: 'en',
            },
        ],
        meta: {
            some_key: 'some_value',
            another_key: 'another_value',
        },
        fields: [
            [
                {
                    type: 'signature',
                    x: '130',
                    y: (totalDescriptionHeight > 160) ? '140' : '620',
                    width: '120',
                    height: '35',
                    page: (totalDescriptionHeight > 160) ? '2' : '1',
                    signer: '1',
                    name: '',
                    identifier: 'unique_field_identifier_1',
                    required: 1,
                    readonly: 0,
                    validation_type: '',
                    text_size: '',
                    text_color: '',
                    text_font: '',
                    text_style: '',
                    value: '',
                    options: [],
                    group: '',
                },
                {
                    type: 'signature',
                    x: '130',
                    y: (totalDescriptionHeight > 160) ? '160' : '680',
                    width: '120',
                    height: '35',
                    page: (totalDescriptionHeight > 160) ? '2' : '1',
                    signer: '2',
                    name: '',
                    identifier: 'unique_field_identifier_2',
                    required: 1,
                    readonly: 0,
                    validation_type: '',
                    text_size: '',
                    text_color: '',
                    text_font: '',
                    text_style: '',
                    value: '',
                    options: [],
                    group: '',
                },
                {
                    type: 'signature',
                    x: '150',
                    y: (totalDescriptionHeight > 160) ? '180' : '720',
                    width: '150',
                    height: '35',
                    page: (totalDescriptionHeight > 160) ? '2' : '1',
                    signer: '3',
                    name: '',
                    identifier: 'unique_field_identifier_3',
                    required: 1,
                    readonly: 0,
                    validation_type: '',
                    text_size: '',
                    text_color: '',
                    text_font: '',
                    text_style: '',
                    value: '',
                    options: [],
                    group: '',
                },
                {
                    type: 'text',
                    x: '370',
                    y: (totalDescriptionHeight > 160) ? '150' : '630',
                    width: '150',
                    height: '17',
                    page: (totalDescriptionHeight > 160) ? '2' : '1',
                    signer: '1',
                    name: 'Full Name',
                    identifier: 'unique_field_identifier_4',
                    required: 0,
                    readonly: 0,
                    validation_type: '',
                    text_size: '',
                    text_color: '',
                    text_font: '',
                    text_style: '',
                    value: ' ',
                    options: [],
                    group: '',
                },
                {
                    type: 'text',
                    x: '370',
                    y: (totalDescriptionHeight > 160) ? '170' : '690',
                    width: '150',
                    height: '17',
                    page: (totalDescriptionHeight > 160) ? '2' : '1',
                    signer: '2',
                    name: 'Full Name',
                    identifier: 'unique_field_identifier_5',
                    required: 0,
                    readonly: 0,
                    validation_type: '',
                    text_size: '',
                    text_color: '',
                    text_font: '',
                    text_style: '',
                    value: ' ',
                    options: [],
                    group: '',
                },
                {
                    type: 'text',
                    x: '370',
                    y: (totalDescriptionHeight > 160) ? '190' : '730',
                    width: '150',
                    height: '17',
                    page: (totalDescriptionHeight > 160) ? '2' : '1',
                    signer: '3',
                    name: 'Full Name',
                    identifier: 'unique_field_identifier_6',
                    required: 0,
                    readonly: 0,
                    validation_type: '',
                    text_size: '',
                    text_color: '',
                    text_font: '',
                    text_style: '',
                    value: ' ',
                    options: [],
                    group: '',
                },
                {
                    type: 'date_signed',
                    x: '470',
                    y: (totalDescriptionHeight > 160) ? '150' : '630',
                    width: '100',
                    height: '17',
                    page: (totalDescriptionHeight > 160) ? '2' : '1',
                    signer: '1',
                    name: 'Date',
                    identifier: 'unique_field_identifier_7',
                    required: 0,
                    readonly: 1,
                    validation_type: '',
                    text_size: '',
                    text_color: '',
                    text_font: '',
                    text_style: '',
                    value: ' ',
                    options: [],
                    group: '',
                },
                {
                    type: 'date_signed',
                    x: '470',
                    y: (totalDescriptionHeight > 160) ? '170' : '690',
                    width: '100',
                    height: '17',
                    page: (totalDescriptionHeight > 160) ? '2' : '1',
                    signer: '2',
                    name: 'Date',
                    identifier: 'unique_field_identifier_8',
                    required: 0,
                    readonly: 1,
                    validation_type: '',
                    text_size: '',
                    text_color: '',
                    text_font: '',
                    text_style: '',
                    value: ' ',
                    options: [],
                    group: '',
                },
                {
                    type: 'date_signed',
                    x: '470',
                    y: (totalDescriptionHeight > 160) ? '190' : '730',
                    width: '100',
                    height: '17',
                    page: (totalDescriptionHeight > 160) ? '2' : '1',
                    signer: '3',
                    name: 'Date',
                    identifier: 'unique_field_identifier_9',
                    required: 0,
                    readonly: 1,
                    validation_type: '',
                    text_size: '',
                    text_color: '',
                    text_font: '',
                    text_style: '',
                    value: ' ',
                    options: [],
                    group: '',
                },
            ],
        ],
    }))
    .then(res => {
        console.log(res);
        resolve(res);
    })
    .catch(err => {
        console.error(err);
        reject(err);
    })
    } else {
         // PUT BLOB INTO FIREBASE STORAGE
    uploadFile(
        `orders/${order.id}/${fileID}`,
        blobPDF
    ) // CREATE SIGNABLE DOCUMENT WITH EVERGRANDE 
        .then(fileURL => {
            console.log(fileURL);
            resolve({
                fileURL: fileURL
            });
        })
        .catch(err => {
            console.error(err);
            reject(err);
        })
    }

   
})

const createRateCon = (tripInfo, orders) => new Promise(async (resolve, reject) => {
    const {
        doc,
        pdfGen,
        width,
        height,
        innerWidth,
        innerHeight
    } = normalPDF();

    // const orders = (await Promise.all(orderIDs.map(id => db.collection('haney-orders').doc(id).get()))).map(doc => ({ id: doc.id, ...doc.data() }));

    const esignEnabled = tripInfo.esignEnabled

    const vendorPickUp = orders[0].vendorPickUp ? orders[0].vendorPickUp : {};

    // PDF HEADER
    const header = [
        {
            type: TYPE_IMAGE,
            base64: LOGO,
            width: 70,
            height: 30,
            x: X_MARGIN,
            y: Y_MARGIN - 10,
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts:
                [
                    'Rate Confirmation',
                    'CargoRx, Ltd',
                ],
            x: width / 2,
            y: Y_MARGIN,
            style: {
                textAlign: 'center',
                textColor: 'black',
                fontWeight: 'bold',
            },
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts: [
                '303-690-1618',
                '6880 E 56th Ave, Commerce City, CO 80022'
            ],
            x: width - X_MARGIN,
            y: Y_MARGIN,
            style: {
                textAlign: 'right',
                textColor: 'black',
                fontWeight: 'bold',
            },
        },
    ];

    // CARGORX INFORMATION
    const cargoRxInformation = {
        y: 85,
        DOT: tripInfo.tripType.includes('Broker') ? ' 3389597' : ' 3389597',
        MC: tripInfo.tripType.includes('Broker') ? ' 1089162' : ' 1089162',
        Contact: '',
        Phone: '303-690-1618',
        email: 'operations@cargorx.com',
        CSN: tripInfo.tripNumber,
    };

    const cargoRxInformationData = [
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 22,
            x: X_MARGIN,
            y: 46,
            style: {
                backgroundColor: primaryColor,
            },
        },
        {
            type: TYPE_TEXT,
            text: 'CargoRx Information',
            x: X_MARGIN + 2,
            y: 60,
            style: {
                textColor: 'white',
            },
        },
        {
            type: TYPE_TEXT,
            text: 'Trip: ' + cargoRxInformation.CSN,
            x: width / 2,
            y: 60,
            style: {
                textColor: 'white',
            },
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts: [
                'DOT #: ' + cargoRxInformation.DOT,
                'MC #: ' + cargoRxInformation.MC,
            ],
            x: X_MARGIN,
            y: cargoRxInformation.y,
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts: [
                'Contact: ' + cargoRxInformation.Contact,
                'Phone: ' + cargoRxInformation.Phone,
            ],
            x: X_MARGIN + innerWidth / 4,
            y: cargoRxInformation.y,
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts: [
                'Email: ' + cargoRxInformation.email,
            ],
            x: X_MARGIN + innerWidth / 2,
            y: cargoRxInformation.y,
        },
    ];

    // VENDOR INFORMATION
    const carrierInformation = {
        y: 155,
        cName: vendorPickUp.name ? vendorPickUp.name : '',
        address: vendorPickUp.physicalAddress ? vendorPickUp.physicalAddress : '',
        city_state_zip: `${vendorPickUp.physicalCity}, ${vendorPickUp.physicalState} ${vendorPickUp.physicalZip}`,
        carrier_phone: vendorPickUp.mainPhone ? vendorPickUp.mainPhone : '',
        DOT: ` ${vendorPickUp.dotNumber ? vendorPickUp.dotNumber : ''}`,
        MC: ` ${vendorPickUp.mcmxffNumber ? vendorPickUp.mcmxffNumber : ''}`,
        additionalTI: tripInfo.additionalTI ? tripInfo.additionalTI : '',
        driver: tripInfo.driver ? tripInfo.driver.name : '',
        driver_phone: tripInfo.driver ? tripInfo.driver.cellPhone : '',
        // driver_phone: pickUpDriver.cell ? pickUpDriver.cell : "",
        carrier_rate: tripInfo.tripRate,
        authorization: '',
        signature: '',
    };

    const carrierInformationData = [
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 22,
            x: X_MARGIN,
            y: carrierInformation.y - 35,
            style: {
                backgroundColor: primaryColor,
            },
        },
        {
            type: TYPE_TEXT,
            text: 'Carrier Information',
            x: X_MARGIN + 2,
            y: carrierInformation.y - 20,
            style: {
                textColor: 'white',
            },
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts: [
                'Carrier Name: ' + carrierInformation.cName,
                'Address: ' + carrierInformation.address,
                '' + carrierInformation.city_state_zip,
                'Carrier Phone: ' + carrierInformation.carrier_phone,
                'Additional Trip Information: ',
                carrierInformation.additionalTI.substring(0, 100),
                carrierInformation.additionalTI.substring(100, 200),
                carrierInformation.additionalTI.substring(200, 300),
            ],
            x: X_MARGIN,
            y: carrierInformation.y,
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts: ['', '', ''],
            x: X_MARGIN + innerWidth / 4,
            y: carrierInformation.y + 12,
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts: [
                'DOT #: ' + carrierInformation.DOT,
                'MC #: ' + carrierInformation.MC,
                'Driver: ' + carrierInformation.driver,
                'Driver Phone: ' + carrierInformation.driver_phone,
            ],
            x: X_MARGIN + innerWidth / 2,
            y: carrierInformation.y,
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts: [
                'Carrier Rate: $' + carrierInformation.carrier_rate,
                'Authorization  ' + carrierInformation.authorization,
                'Signature: ' + carrierInformation.signature,
            ],
            x: X_MARGIN + (innerWidth / 4) * 3,
            y: carrierInformation.y,
        },
    ];

    // Y OFFSET VARIABLES
    const orderOffset = 116;
    const itemOffset = 50;
    let itemsAdded, ordersAdded;
    itemsAdded = ordersAdded = 0;

    // Y OFFSET CALCULATOR
    const yHeight = (i) => (i > 0 ? 85 : 285) + orderOffset * ordersAdded + itemOffset * itemsAdded;

    // TOTAL TRIP VALUES FOR ALL ORDER ITEMS
    let totalWeight, totalQuantity, totalValue;
    totalWeight = totalQuantity = totalValue = 0;

    const deliveryDate = (order) => {
        const firstDate = order.dropOffBy || (tripInfo.isDropOff && tripInfo.dropOffBy) || null;

        if (firstDate) return moment(firstDate).format('YYYY-MM-DD HH:mm');

        const secondDate = ['air', 'cargoAir', 'ground', 'ltl', 'autoLtl'].reduce((prev, curr) => {
            const dateObj = order[curr];
            if (dateObj && dateObj.quoteETA && dateObj.quoteETA.toDate().valueOf() === order.serviceETA.toDate().valueOf()) return dateObj;
            return prev;
        }, {});

        if (secondDate.quoteETA) return `${moment(secondDate.quoteETA.toDate()).format('YYYY-MM-DD')} ${[secondDate.start, secondDate.end].filter(Boolean).join(' - ')}`;

        return moment(order.destination.date.toDate()).format('YYYY-MM-DD');
    }

    // DATA FOR ALL ORDERS IN TRIP
    const stops = orders.map((order, i) => {

        // TOTAL ITEM VALUES FOR SINGLE ORDER
        let stopQuantity, stopWeight, stopValue;
        stopQuantity = stopWeight = stopValue = 0;

        // CALCULATE VALUES FOR SINGLE ORDER
        for (let i = 0; i < order.items.length; i++) {
            const wpp = parseFloat(order.items[i].weight ? order.items[i].weight : 0);
            const qty = parseInt(order.items[i].quantity ? order.items[i].quantity : 0);
            stopQuantity += qty;
            stopWeight += wpp * qty;
            stopValue += parseFloat(order.items[i].declaredValue ? order.items[i].declaredValue : 0);
        }

        // ADD SINGLE ORDER VALUES TO TOTAL TRIP VALUES
        totalWeight += stopWeight;
        totalQuantity += stopQuantity;
        totalValue += stopValue;

        // EVERY ODD ORDER, RESET Y OFFSET VARIABLES FOR NEW PAGE CREATION
        if (i % 2 === 1) {
            itemsAdded = ordersAdded = 0;
        }

        // ORDER INFORMATION
        const stopInformation = {
            y: yHeight(i),
            pickup: order.freightBillNumber,
            pickup_location: order.isPickUp ?
                order.pickUpLocation ?
                    order.pickUpLocation.entityName
                    : ' '
                : tripInfo.pickUpLocation && tripInfo.isPickUp ?
                    tripInfo.pickUpLocation.entityName
                    : order.origin.businessName ? order.origin.businessName : '',
            address: order.isPickUp ?
                order.pickUpLocation ?
                    order.pickUpLocation.address
                    : ' '
                : tripInfo.pickUpLocation && tripInfo.isPickUp
                    ? tripInfo.pickUpLocation.address
                    : order.origin.address
                        ? order.origin.address
                        : '',
            city_state_zip: order.isPickUp ?
                order.pickUpLocation ?
                    `${order.pickUpLocation.city} ${order.pickUpLocation.state} ${order.pickUpLocation.zip}`
                    : ' '
                : tripInfo.pickUpLocation && tripInfo.isPickUp ?
                    `${tripInfo.pickUpLocation.city}, ${tripInfo.pickUpLocation.state} ${tripInfo.pickUpLocation.zip}`
                    : `${order.origin.city}, ${order.origin.state} ${order.origin.zip}`,
            carrier_phone: order.origin.phone ? order.origin.phone : '',
            customer_extra_service: order.origin.customExtraService ? order.origin.customExtraService : '',
            services: order.origin.services.join(' : '),
            pickup_date: order.pickUpBy ?
                moment(order.pickUpBy).format('YYYY-MM-DD HH:mm')
                : `${moment(tripInfo.pickUpBy && tripInfo.isPickUp ?
                    tripInfo.pickUpBy
                    : order.origin.date.toDate()
                ).format('YYYY-MM-DD')} ${[order.origin.startTimeWindow, order.origin.endTimeWindow].join(' - ')}`,
            business_name: order.isDropOff ?
                order.dropOffLocation ?
                    order.dropOffLocation.entityName
                    : ' '
                : tripInfo.dropOffLocation && tripInfo.isDropOff ?
                    tripInfo.dropOffLocation.entityName
                    : order.destination.businessName ?
                        order.destination.businessName
                        : '',
            delivery_address: order.isDropOff ?
                order.dropOffLocation ?
                    order.dropOffLocation.address
                    : ' '
                : tripInfo.dropOffLocation && tripInfo.isDropOff ?
                    tripInfo.dropOffLocation.address
                    : order.destination.address ?
                        order.destination.address
                        : '',
            delivery_city_state_zip: order.isDropOff ?
                order.dropOffLocation ?
                    `${order.dropOffLocation.city} ${order.dropOffLocation.state} ${order.dropOffLocation.zip}`
                    : ' '
                : tripInfo.dropOffLocation && tripInfo.isDropOff ?
                    `${tripInfo.dropOffLocation.city}, ${tripInfo.dropOffLocation.state} ${tripInfo.dropOffLocation.zip}`
                    : `${order.destination.city}, ${order.destination.state} ${order.destination.zip}`,
            delivery_carrier_phone: order.destination.phone ? order.destination.phone : '',
            signature: '',
            delivery_customer_extra_service: order.destination.customExtraService ? order.destination.customExtraService : '',
            delivery_services: order.origin.services.join(' : '),
            delivery_date: deliveryDate(order),
            declared_value: stopValue ? stopValue.toString() : '',
            sum_weight: stopWeight.toString(),
            sum_quantity: stopQuantity.toString()
        };

        const stopInformationData = [
            {
                type: TYPE_RECT,
                width: width - 2 * X_MARGIN,
                height: 22,
                x: X_MARGIN,
                y: stopInformation.y - 35,
                style: {
                    backgroundColor: primaryColor,
                },
            },
            {
                type: TYPE_TEXT,
                text: `Stop ${i + 1} Information`,
                x: X_MARGIN + 2,
                y: stopInformation.y - 20,
                style: {
                    textColor: 'white',
                },
            },
            {
                type: TYPE_MULTI_LINE_TEXT,
                texts: [
                    'Shipment #: ' + stopInformation.pickup,
                    'Pick Up Date: ' + stopInformation.pickup_date,
                    'Location: ' + stopInformation.pickup_location,
                    'Address: ' + stopInformation.address,
                    '' + stopInformation.city_state_zip,
                    'Phone: ' + stopInformation.carrier_phone,
                    '' + doc.splitTextToSize(stopInformation.customer_extra_service.substring(0, 50), 100),
                    '' + stopInformation.services,
                    'Declared Value: ' + stopInformation.declared_value,
                ],
                x: X_MARGIN,
                y: stopInformation.y,
            },
            {
                type: TYPE_MULTI_LINE_TEXT,
                texts: [
                    '',
                    'Delivery Date: ' + stopInformation.delivery_date,
                    'Delivery Location: ' + stopInformation.business_name,
                    'Address: ' + stopInformation.delivery_address,
                    '' + stopInformation.delivery_city_state_zip,
                    'Phone: ' + stopInformation.delivery_carrier_phone,
                    '' + doc.splitTextToSize(stopInformation.delivery_customer_extra_service.substring(0, 50), 100),
                    '' + stopInformation.delivery_services,
                    `Total Weight: ${stopInformation.sum_weight}          Total Quantity: ${stopInformation.sum_quantity}`,
                ],
                x: X_MARGIN + innerWidth / 2,
                y: stopInformation.y,
            },
        ];

        ordersAdded += 1;

        // FREIGHT/ITEMS INFORMATION
        const freightInfo = order.items.map((item, index) => {
            const itemInfo = {
                y: yHeight(i),
                pieces: item.quantity ? item.quantity : '',
                description: item.description ? item.description : '',
                weight: item.weight ? item.weight : '',
                length: item.length ? item.length : '',
                width: item.width ? item.width : '',
                height: item.height ? item.height : '',
                haz: item.isHazmat ? 'Yes' : 'No',
                un_num: item.unNumber ? item.unNumber : '',
                totalWeight,
                totalQuantity
            };

            itemsAdded += 1;

            return itemInfo;
        });

        const freightInfoData = freightInfo.map((itemInfo) => [
            {
                type: TYPE_TEXT,
                text: 'Pieces',
                x: X_MARGIN,
                y: itemInfo.y,
                style: {
                    textColor: 'black',
                    fontWeight: 'bold',
                },
            },
            {
                type: TYPE_TEXT,
                text: 'Description',
                x: X_MARGIN + innerWidth / 12,
                y: itemInfo.y,
                style: {
                    textColor: 'black',
                    fontWeight: 'bold',
                },
            },
            {
                type: TYPE_TEXT,
                text: 'Weight',
                x: X_MARGIN + 6 * (innerWidth / 12),
                y: itemInfo.y,
                style: {
                    textColor: 'black',
                    fontWeight: 'bold',
                },
            },
            {
                type: TYPE_TEXT,
                text: 'Length',
                x: X_MARGIN + 7 * (innerWidth / 12),
                y: itemInfo.y,
                style: {
                    textColor: 'black',
                    fontWeight: 'bold',
                },
            },
            {
                type: TYPE_TEXT,
                text: 'Width',
                x: X_MARGIN + 8 * (innerWidth / 12),
                y: itemInfo.y,
                style: {
                    textColor: 'black',
                    fontWeight: 'bold',
                },
            },
            {
                type: TYPE_TEXT,
                text: 'Height',
                x: X_MARGIN + 9 * (innerWidth / 12),
                y: itemInfo.y,
                style: {
                    textColor: 'black',
                    fontWeight: 'bold',
                },
            },
            {
                type: TYPE_TEXT,
                text: 'Haz',
                x: X_MARGIN + 10 * (innerWidth / 12),
                y: itemInfo.y,
                style: {
                    textColor: 'black',
                    fontWeight: 'bold',
                },
            },
            {
                type: TYPE_TEXT,
                text: 'UN Num',
                x: X_MARGIN + 11 * (innerWidth / 12),
                y: itemInfo.y,
                style: {
                    textColor: 'black',
                    fontWeight: 'bold',
                },
            },
        ]);

        const freightInfoValueData = freightInfo.map((itemInfo) => [
            {
                type: TYPE_TEXT,
                text: itemInfo.pieces,
                x: X_MARGIN,
                y: itemInfo.y + 16,
            },
            {
                type: TYPE_MULTI_LINE_TEXT,
                texts: doc.splitTextToSize(itemInfo.description, 350),
                x: X_MARGIN + innerWidth / 12,
                y: itemInfo.y + 16,
            },
            {
                type: TYPE_TEXT,
                text: itemInfo.weight,
                x: X_MARGIN + 6 * (innerWidth / 12),
                y: itemInfo.y + 16,
            },
            {
                type: TYPE_TEXT,
                text: itemInfo.length,
                x: X_MARGIN + 7 * (innerWidth / 12),
                y: itemInfo.y + 16,
            },
            {
                type: TYPE_TEXT,
                text: itemInfo.width,
                x: X_MARGIN + 8 * (innerWidth / 12),
                y: itemInfo.y + 16,
            },
            {
                type: TYPE_TEXT,
                text: itemInfo.height,
                x: X_MARGIN + 9 * (innerWidth / 12),
                y: itemInfo.y + 16,
            },
            {
                type: TYPE_TEXT,
                text: itemInfo.haz,
                x: X_MARGIN + 10 * (innerWidth / 12),
                y: itemInfo.y + 16,
            },
            {
                type: TYPE_TEXT,
                text: itemInfo.un_num,
                x: X_MARGIN + 11 * (innerWidth / 12),
                y: itemInfo.y + 16,
            }
        ]);

        // PUT ALL FREIGHT DATA IN ONE ARRAY
        let freightData = [];
        for (let i = 0; i < order.items.length; i++) {
            freightData = [
                ...freightData,
                ...freightInfoData[i],
                ...freightInfoValueData[i],
            ];
        }

        return [...stopInformationData, ...freightData];
    });

    // GET ALL STANDARD PAGE 1 DATA
    let pageNumber = 1;
    let data = [
        ...header,
        ...cargoRxInformationData,
        ...carrierInformationData,
    ];

    // ADD STANDARD DATA AND FREIGHT TO PAGES, SWITCHING PAGES EVERY OTHER STOP/ORDER
    stops.map((stop, i, self) => {
        if (i % 2 === 1) {
            pdfGen.addDataToPage(data, doc);
            pageNumber += 1;
            doc.addPage();
            doc.setPage(pageNumber);
            data = [...header, ...stop];
        } else {
            data = [...data, ...stop];
        }

        if (i === self.length - 1) {
            data.push(
                {
                    type: TYPE_RECT,
                    width: width - 2 * X_MARGIN,
                    height: 22,
                    x: X_MARGIN,
                    y: data[data.length - 1].y + 60,
                    style: {
                        backgroundColor: primaryColor,
                    },
                },
                {
                    type: TYPE_TEXT,
                    text: 'Total Quantity:',
                    x: (X_MARGIN + 2),
                    y: data[data.length - 1].y + 75,
                    style: {
                        textColor: 'white',
                        fontWeight: 'bold',
                    },
                },
                {
                    type: TYPE_TEXT,
                    text: 'Total Weight:',
                    x: (X_MARGIN + 2) + 5 * (innerWidth / 12),
                    y: data[data.length - 1].y + 75,
                    style: {
                        textColor: 'white',
                        fontWeight: 'bold',
                    },
                },
                {
                    type: TYPE_TEXT,
                    text: 'Total Value:',
                    x: (X_MARGIN + 2) + 10 * (innerWidth / 12),
                    y: data[data.length - 1].y + 75,
                    style: {
                        textColor: 'white',
                        fontWeight: 'bold',
                    },
                },
                {
                    type: TYPE_TEXT,
                    text: totalQuantity.toString(),
                    x: X_MARGIN + 2,
                    y: data[data.length - 1].y + 95,
                    style: {
                        textColor: 'black',
                    },
                },
                {
                    type: TYPE_TEXT,
                    text: totalWeight.toString(),
                    x: (X_MARGIN + 2) + 5 * (innerWidth / 12),
                    y: data[data.length - 1].y + 95,
                    style: {
                        textColor: 'black',
                    },
                },
                {
                    type: TYPE_TEXT,
                    text: totalValue.toString(),
                    x: (X_MARGIN + 2) + 10 * (innerWidth / 12),
                    y: data[data.length - 1].y + 95,
                    style: {
                        textColor: 'black',
                    },
                }
            );
            pdfGen.addDataToPage(data, doc)
        };
    });

    // LAST PAGE STATIC INFO
    pageNumber += 1;
    doc.addPage();
    doc.setPage(pageNumber);

    const billingInfoData = [
        {
            type: TYPE_RECT,
            width: width - 2 * X_MARGIN,
            height: 22,
            x: X_MARGIN,
            y: Y_MARGIN + 25,
            style: {
                backgroundColor: primaryColor,
            },
        },
        {
            type: TYPE_TEXT,
            text: 'Requirements & Billing Information',
            x: X_MARGIN + 2,
            y: Y_MARGIN + 40,
            style: {
                textColor: 'white',
            },
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts: [
                'All services of Carrier are subject to the Carrier/Broker Agreement between Carrier and CargoRx, Ltd., as well as the terms',
                'and conditions herein. This rate includes all stop-off charges, fuel surcharges, loading, unloading, etc. This rate cannot be',
                'changed, modified, or supplemented by reference to any other rates, rules, classification, schedule, or tariff. Carrier shall',
                "be liable for full loss resulting from loss, damage, injury, or delay, except that Carrier's liability shall not exceed",
                "$100,000.00 per trailer or conveyance unless a higher value is declared below or on the bill of lading, in which case,",
                "Carrier's liability shall be lesser of the full value of the goods involved, or the declared value. Full loss is the invoice price of",
                "freight tendered to the Carrier for transport. A minimum of $100,000 cargo insurace is require unless otherwise noted.",
                '',
                '',
                'The detention rate paid is $35 per hour after 3 hours.  CargoRx, Ltd. must be contacted within 1 hour after the scheduled',
                'appointment time or arrival time.  IN and OUT times must be clearly noted on the Bill of Lading (BOL) or Proof of Delivery',
                '(POD) and signed by the shipper and or receiver.  Layover charges are not to exceed $150.00 per day and will not include',
                'weekend days unless previously agreed to.',
                '',
                '',
                "- It is the Driver's responsibility to ensure that the load is safe, secure and legal for transport",
                '- Driver must fully utilize the CargoLink sent via text to complete/upload shipping documents, take photos of the freight, and update',
                '  the status of the orders. A penalty of $300 will be issued on all trips where the CargoLink was not fully utilized.',
                '- All trailers must be clean, empty and odor free with no holes',
                '- Any deviation from dispatch instructions must be called in immediately',
                '- All products, shortages and/or overages, must be reported at the time of delivery - failure to report will result in additional',
                '  charges.',
                '- Brokering, assigning, or interlining of this shipment will void our obligation to pay your freight charge.',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                'Signed Rate Confirmation',
                'Shipper Signed House bill of lading',
                "Proof of delivery including receiver's name and the date of delivery listed within the space provided on the bill of lading",
                'Please submit freight bill and supporting documentation via weblink, by email to                                                              ',
                'or mail to CargoRx, Ltd, Attn:                                                           \t\t\t\t\t\t.',
                'The CargoRx shipment number must be referenced on all hardcopy invoices and supporting documentation.',
                'Terms are net 30 unless otherwise agreed upon prior to the shipment is accepted by Carrier.',
                '',
                '',
                '',
                '',
                'We understand that this contract has been approved by a person authorized to do so. If any information is incorrect, please',
                'contact us by fax or telephone before executing the above contract.',
                'Confirmation must be signed and faxed to Broker before Loading. All truck invoices must be presented for payment with',
                'original Bill of Lading proof of deliveries, and a signed copy of this rate confirmation.',
                "- For sealed loads, seal numbers and 'Seal Intact' notation must appear on Bill of Lading",
                '- Driver must count during loading or get SLC notation on Bill of Lading',
            ],
            x: X_MARGIN,
            y: 85,
            style: {
                fontSize: 9.5,
            },
        },
        // the bolder texts
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts: [
                "Each of the below required documents must be submitted through CargoRx's CargoLink which is sent to the driver via",
                'text. Hard copy documents must be mailed in with the invoice, or emailed with the invoice to',
                'payables@cargorx.com. All required documentation must be uploaded via the CargoLink within 12 hours of delivery.',
                "CargoRx, Ltd. will deduct $100 from the amount shown on this Rate Confirmation for every 24 hours past delivery",
                "the paperwork remains absent.",
            ],
            x: X_MARGIN,
            y: Y_MARGIN + innerHeight / 2 - 10,
            style: {
                textColor: 'black',
                fontWeight: 'bold',
                fontSize: 9.5,
            },
        },
        {
            type: TYPE_TEXT,
            text: 'payables@cargorx.com',
            x: X_MARGIN + innerWidth - innerWidth / 4 - 68,
            y: Y_MARGIN + innerHeight / 2 + 85,
            style: {
                textColor: 'black',
                fontWeight: 'bold',
                fontSize: 9.5,
            },
        },
        {
            type: TYPE_TEXT,
            text: 'Accounts Payable, 6880 E 56th Ave, Commerce City, CO 80022',
            x: X_MARGIN + 130,
            y: Y_MARGIN + innerHeight / 2 + 97,
            style: {
                textColor: 'black',
                fontWeight: 'bold',
                fontSize: 9.5,
            },
        },
        {
            type: TYPE_MULTI_LINE_TEXT,
            texts: [
                'BY ACCEPTING THE SHIPMENT, CARRIER AGREES THAT THE DRIVER HAS CONSENTED TO RECEIVE TEXT MESSAGES AND/OR',
                'PHONE CALLS FROM OR ON BEHALF OF CargoRx., Ltd.',
            ],
            x: X_MARGIN,
            y: Y_MARGIN + (innerHeight / 3) * 2 + 30,
            style: {
                textColor: 'black',
                fontWeight: 'bold',
                fontSize: 8.5,
            },
        },
    ];

    data = [...header, ...billingInfoData];
    pdfGen.addDataToPage(data, doc);

    // TURN PDF TO BLOB, CREATE RANDOM ID FOR NEW FILE
    const blobPDF = new Blob([doc.output('blob')], { type: 'application/pdf' });
    const fileID = uuidv1();

    if (esignEnabled) {
        // PUT BLOB INTO FIREBASE STORAGE
    uploadFile(
        `trips/${tripInfo.tripID}/${fileID}`,
        blobPDF
    ) // CREATE SIGNABLE DOCUMENT WITH EVERGRANDE 
        .then(fileURL => createSignable({
            sandbox: 0,
            is_draft: 0,
            embedded: 1,
            title: `Trip for ${orders.map((order) => order.freightBillNumber).join(', ')}`,
            message: 'This is my general document message.',
            use_signer_order: 1,
            reminders: 0,
            require_all_signers: 1,
            custom_requester_name: '',
            custom_requester_email: '',
            redirect: `https://cargo-rx-vendor.web.app/trips/${tripInfo.tripID}?pickUpConfirmed=Y`,
            redirect_decline: 'https://cargo-rx-vendor.web.app/declined',
            client: '',
            expires: 1750513480,
            embedded_signing_enabled: 1,
            flexible_signing: 0,
            use_hidden_tags: 0,
            files: [
                {
                    name: 'Trip Rate Con',
                    file_url: fileURL,
                    file_id: '',
                    file_base64: '',
                },
            ],
            signers: [
                {
                    id: 1,
                    name: ' ',
                    email: 'vendoralerts@haneyfreightways.com',
                    order: 1,
                    role: tripInfo.tripID,
                    // "pin": "3874",
                    message: 'This is my custom message to Signer.',
                    deliver_email: 0,
                    language: 'en',
                },
            ],
            recipients: [
                {
                    name: 'Vendor Alerts',
                    email: 'vendoralerts@haneyfreightways.com',
                },
            ],
            meta: {
                some_key: 'some_value',
                another_key: 'another_value',
            },
            fields: [
                [
                    {
                        type: 'signature',
                        x: `${X_MARGIN + (innerWidth / 4) * 3 + 50}`,
                        y: `${carrierInformation.y + 15}`,
                        width: '80',
                        height: '30',
                        page: '1',
                        signer: '1',
                        name: '',
                        identifier: 'unique_field_identifier_1',
                        required: 1,
                        readonly: 0,
                        validation_type: '',
                        text_size: '',
                        text_color: '',
                        text_font: '',
                        text_style: '',
                        value: '',
                        options: [],
                        group: '',
                    },
                ],
            ],
        }))
        .then(res => {
            console.log(res);
            resolve({
                ...res,
                fileID
            });
        })
        .catch(err => {
            console.error(err);
            reject(err);
        })
    } else {
        uploadFile(
            `trips/${tripInfo.tripID}/${fileID}`,
            blobPDF
        ) // CREATE SIGNABLE DOCUMENT WITH EVERGRANDE 
            .then(fileURL => {
                console.log(fileURL);
                resolve({
                    fileURL: fileURL
                });
            })
            .catch(err => {
                console.error(err);
                reject(err);
            })
    }

    

})

export {
    uploadFile,
    createSignable,
    createBOL,
    createRateCon,
}


// WEBPACK FOOTER //
// ./src/store/actions/documentActions.jsx